import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import PokeFriendsModal from '../components/PokeFriendsModal';
import CampaignIcon from '@mui/icons-material/Campaign';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    IconButton,
    Avatar,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Chip,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Menu,
    MenuItem,
    InputAdornment,
    Alert,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Tooltip,
    AvatarGroup,
    Collapse,
    CircularProgress,
    Snackbar,
} from '@mui/material';
import {
    ArrowBack,
    PersonAdd,
    Group,
    Search,
    MoreVert,
    Delete,
    Edit,
    Email,
    Check,
    Close,
    Send,
    GroupAdd,
    Person,
    People,
    ExpandMore,
    ExpandLess,
    Flight,
    PhotoLibrary,
    EventNote,
    Login,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod';

// ============ INTERFACES ============
interface Friend {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    status: 'active' | 'pending_sent' | 'pending_received';
    addedDate: string;
    canSeeTrips: boolean;
    canSeePhotos: boolean;
    canSeeItinerary: boolean;
}

interface TravelGroup {
    id: string;
    name: string;
    description?: string;
    members: string[];
    createdDate: string;
    tripCount: number;
    color: string;
}


// ============ HELPER FUNCTIONS ============
// Read the current user's email from the idToken (case-insensitive consumer should normalize).
// Returns null if no token, malformed token, or no email claim.
// (#217 Part 1 — used by inviter-self-match guard)
const getCurrentUserEmailFromToken = (): string | null => {
    try {
        const token = localStorage.getItem('idToken');
        if (!token) return null;
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = JSON.parse(atob(parts[1]));
        return payload.email ? String(payload.email).toLowerCase() : null;
    } catch {
        return null;
    }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getStatusChip = (status: Friend['status']) => {
    switch (status) {
        case 'active':
            return <Chip label="Connected" size="small" sx={{ bgcolor: '#E8F5F3', color: '#2D6E6A', fontWeight: 500 }} />;
        case 'pending_sent':
            return <Chip label="Invite Sent" size="small" sx={{ bgcolor: '#FFF3E0', color: '#FF9800', fontWeight: 500 }} />;
        case 'pending_received':
            return <Chip label="Pending" size="small" sx={{ bgcolor: '#E3F2FD', color: '#2196F3', fontWeight: 500 }} />;
    }
};

// ============ MAIN COMPONENT ============
const FriendsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isEnabled } = useFeatureFlags();
    const groupTripsEnabled = isEnabled('group_trips');

    // Authentication state
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    // (#217 Part 1) Renamed semantics: this is the INVITER's email displayed on the banner,
    // not the invitee's. Previously the wrong value was being set here.
    const [pendingInviterEmail, setPendingInviterEmail] = useState<string | null>(null);

    const [activeTab, setActiveTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [friends, setFriends] = useState<Friend[]>([]);
    const [groups, setGroups] = useState<TravelGroup[]>([]);
    const [friendsLoading, setFriendsLoading] = useState(true);
    // Dialog states
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);
    const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
    const [loginPromptOpen, setLoginPromptOpen] = useState(false);
    const [selectedFriend, setSelectedFriend] = useState<Friend | null>(null);
    const [selectedGroup, setSelectedGroup] = useState<TravelGroup | null>(null);

    // Form states
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteMessage, setInviteMessage] = useState('');
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupDescription, setNewGroupDescription] = useState('');
    const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
    const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

    // Menu states
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuFriendId, setMenuFriendId] = useState<string | null>(null);

    // API states
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [pokeModalOpen, setPokeModalOpen] = useState(false);
    const [emailMismatch, setEmailMismatch] = useState<{expectedEmail: string; inviterEmail: string} | null>(null);
// ============ FETCH FRIENDS FROM API ============
const fetchFriends = useCallback(async () => {
    try {
        const token = localStorage.getItem('idToken');
        if (!token) {
            setFriendsLoading(false);
            return;
        }

        const response = await fetch(`${API_URL}/friends/list`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        const data = await response.json();

        if (response.ok && data.success) {
            setFriends(data.friends);
        } else {
            console.error('Failed to fetch friends:', data.error);
        }
    } catch (error) {
        console.error('Error fetching friends:', error);
    } finally {
        setFriendsLoading(false);
    }
}, []);
const processPendingInvite = useCallback(async () => {
    try {
        const token = localStorage.getItem('idToken');
        if (!token) return;

        const storedInvite = localStorage.getItem('pendingFriendInvite');
        if (!storedInvite) return;

        let inviterEmail: string;
        let inviteeEmail: string | null = null;

        try {
            const parsed = JSON.parse(storedInvite);
            inviterEmail = parsed.inviterEmail;
            inviteeEmail = parsed.inviteeEmail;
        } catch {
            // Legacy format - just the inviter email as string
            inviterEmail = storedInvite;
        }

        // Defense-in-depth self-match guard. The useEffect below also guards,
        // but if anything bypasses that path we still won't fire accept against self.
        const currentUserEmail = getCurrentUserEmailFromToken();
        if (currentUserEmail && currentUserEmail === inviterEmail.toLowerCase()) {
            localStorage.removeItem('pendingFriendInvite');
            setPendingInviterEmail(null);
            return;
        }

        const response = await fetch(`${API_URL}/friends/accept`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                inviterEmail,
                inviteeEmail
            }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
            setSnackbarMessage(data.message || `Connected with ${inviterEmail}!`);
            setSnackbarOpen(true);
            // Refresh friends list
            fetchFriends();
            // Clear the stored invite
            localStorage.removeItem('pendingFriendInvite');
            setPendingInviterEmail(null);
        } else if (data.error === 'EMAIL_MISMATCH') {
            // User is logged in as wrong account - show prominent dialog
            setEmailMismatch({ expectedEmail: data.expectedEmail, inviterEmail });
            // Don't clear the invite - let them re-login
        } else {
            console.error('Failed to accept invite:', data.error);
            setSnackbarMessage(data.error || 'Could not process invitation');
            setSnackbarOpen(true);
            // Clear on other errors
            localStorage.removeItem('pendingFriendInvite');
            setPendingInviterEmail(null);
        }

    } catch (error) {
        console.error('Error processing invite:', error);
        setSnackbarMessage('Error processing invitation');
        setSnackbarOpen(true);
    }
}, [fetchFriends]);

   // ============ AUTHENTICATION CHECK ============
   useEffect(() => {
    const token = localStorage.getItem('idToken');
    const inviterParam = searchParams.get('invite'); // Who sent the invite
    const inviteeParam = searchParams.get('to'); // Who should receive it

    // Resolve which email to display on the banner. The banner says
    // "<inviter> wants to connect with you", so we always show the INVITER's email.
    // (#217 Part 1 — fix banner email-display bug)
    let resolvedInviter: string | null = null;
    let resolvedInvitee: string | null = null;

    if (inviterParam && inviteeParam) {
        // Fresh invite link with both params
        localStorage.setItem('pendingFriendInvite', JSON.stringify({
            inviterEmail: inviterParam,
            inviteeEmail: inviteeParam
        }));
        resolvedInviter = inviterParam;
        resolvedInvitee = inviteeParam;
    } else if (inviterParam) {
        // Legacy link without 'to' param - store just inviter
        localStorage.setItem('pendingFriendInvite', JSON.stringify({
            inviterEmail: inviterParam,
            inviteeEmail: null
        }));
        resolvedInviter = inviterParam;
    } else {
        // Check if there's a stored invite from before login
        const storedInvite = localStorage.getItem('pendingFriendInvite');
        if (storedInvite) {
            try {
                const parsed = JSON.parse(storedInvite);
                resolvedInviter = parsed.inviterEmail || null;
                resolvedInvitee = parsed.inviteeEmail || null;
            } catch {
                // Legacy string format
                resolvedInviter = storedInvite;
            }
        }
    }

    // Inviter-self-match guard: if the currently logged-in user IS the inviter,
    // never display the banner (would be "you wants to connect with you" — nonsense).
    // Clear any stale localStorage entry from prior sessions.
    // (#217 Part 1 — prevents the false self-banner bug)
    if (resolvedInviter && token) {
        const currentUserEmail = getCurrentUserEmailFromToken();
        if (currentUserEmail && currentUserEmail === resolvedInviter.toLowerCase()) {
            localStorage.removeItem('pendingFriendInvite');
            setPendingInviterEmail(null);
        } else {
            setPendingInviterEmail(resolvedInviter);
        }
    } else if (resolvedInviter) {
        // No token = unauthenticated. Show inviter on banner via the invited-card flow.
        setPendingInviterEmail(resolvedInviter);
    }

    if (!token) {
        setIsAuthenticated(false);
        // Show login prompt instead of redirecting immediately
        if (inviterParam || resolvedInviter) {
            setLoginPromptOpen(true);
        } else {
            // No invite param, redirect to login
            navigate('/login?redirect=/friends');
        }
    } else {
        setIsAuthenticated(true);
        // Don't auto-process here - let the next useEffect handle it
    }

    // resolvedInvitee is read for symmetry / future use; intentionally unused here
    void resolvedInvitee;
}, [searchParams, navigate]);

// ============ FETCH FRIENDS ON LOAD ============
useEffect(() => {
    if (isAuthenticated) {
        fetchFriends();
        // Note: pendingInviterEmail is set by the auth-check useEffect above
        // (with self-match guard already applied), so we don't re-derive it here.
    }
}, [isAuthenticated, fetchFriends]);


    // ============ COMPUTED VALUES ============
    const activeFriends = friends.filter(f => f.status === 'active');
    const pendingInvites = friends.filter(f => f.status === 'pending_sent' || f.status === 'pending_received');
    const pendingReceived = friends.filter(f => f.status === 'pending_received');
    // (#217 Part 1) Total pending count for the Invites tab badge — shows when
    // either sent OR received invites exist.
    const pendingTotal = pendingInvites.length;

    const filteredFriends = friends.filter(f =>
        f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // ============ HANDLERS ============
    const handleSendInvite = async () => {
        if (!inviteEmail) return;

        setInviteLoading(true);
        setInviteError(null);

        try {
            const token = localStorage.getItem('idToken');
            if (!token) {
                setInviteError('Please log in to send invitations');
                setInviteLoading(false);
                return;
            }

            const response = await fetch(`${API_URL}/friends/invite`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    email: inviteEmail,
                    message: inviteMessage || undefined,
                }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                setInviteError(data.error || 'Failed to send invitation');
                setInviteLoading(false);
                return;
            }

            // Add to local state for immediate UI feedback
            const newFriend: Friend = {
                id: `f${Date.now()}`,
                name: inviteEmail.split('@')[0],
                email: inviteEmail,
                status: 'pending_sent',
                addedDate: new Date().toISOString().split('T')[0],
                canSeeTrips: false,
                canSeePhotos: false,
                canSeeItinerary: false,
            };
            setFriends([...friends, newFriend]);

            // Reset form and close dialog
            setInviteEmail('');
            setInviteMessage('');
            setInviteDialogOpen(false);

            // Clear any pending invite data (in case user was testing)
            localStorage.removeItem('pendingFriendInvite');
            setPendingInviterEmail(null);

            // Show success message
            setSnackbarMessage(data.message || 'Invitation sent!');
            setSnackbarOpen(true);

            // Refresh friends list so the new pending invite appears in Sent Invites
            fetchFriends();

        } catch (error) {
            console.error('Error sending invite:', error);
            setInviteError('Network error. Please try again.');
        } finally {
            setInviteLoading(false);
        }
    };

    const handleAcceptInvite = (friendId: string) => {
        setFriends(friends.map(f =>
            f.id === friendId ? { ...f, status: 'active' as const } : f
        ));
    };

    const handleDeclineInvite = (friendId: string) => {
        setFriends(friends.filter(f => f.id !== friendId));
    };

    const handleRemoveFriend = async (friendId: string, friendEmail?: string) => {
        const token = localStorage.getItem('idToken');
        const friend = friends.find(f => f.id === friendId);
        
        // If it's a pending sent invite, cancel it on the backend first
        if (friend?.status === 'pending_sent' && token) {
            try {
                const body: Record<string, string> = {};
                if (friendEmail) body.friendEmail = friendEmail;
                else if (friend?.email) body.friendEmail = friend.email;
                
                await fetch(`${process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/dev'}/friends/cancel`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(body),
                });
            } catch (err) {
                console.error('Failed to cancel invite:', err);
            }
        }
        
        setFriends(friends.filter(f => f.id !== friendId));
        setGroups(groups.map(g => ({
            ...g,
            members: g.members.filter(m => m !== friendId)
        })));
        setAnchorEl(null);
    };

    const handleCreateGroup = () => {
        if (!newGroupName) return;
        const colors = ['#2D6E6A', '#FF6B6B', '#4A6FA5', '#2196F3', '#FF9800', '#4CAF50'];
        const newGroup: TravelGroup = {
            id: `g${Date.now()}`,
            name: newGroupName,
            description: newGroupDescription,
            members: selectedMembers,
            createdDate: new Date().toISOString().split('T')[0],
            tripCount: 0,
            color: colors[groups.length % colors.length],
        };
        setGroups([...groups, newGroup]);
        setNewGroupName('');
        setNewGroupDescription('');
        setSelectedMembers([]);
        setGroupDialogOpen(false);
    };

    const handleDeleteGroup = (groupId: string) => {
        setGroups(groups.filter(g => g.id !== groupId));
    };

    const handleUpdatePermissions = () => {
        if (!selectedFriend) return;
        setFriends(friends.map(f =>
            f.id === selectedFriend.id ? selectedFriend : f
        ));
        setPermissionsDialogOpen(false);
        setSelectedFriend(null);
    };

    const handleLoginRedirect = () => {
        navigate('/login?redirect=/friends');
    };

    const handleSignupRedirect = () => {
        navigate('/signup?redirect=/friends');
    };

    const getFriendById = (id: string) => friends.find(f => f.id === id);

    // ============ LOADING STATE ============
    if (isAuthenticated === null) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: '#2D6E6A' }} />
            </Box>
        );
    }

    // ============ UNAUTHENTICATED VIEW (with invite) ============
    if (!isAuthenticated && pendingInviterEmail) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFB' }}>
                {/* Header */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #2D6E6A 0%, #008B7A 100%)',
                        color: 'white',
                        pt: 3,
                        pb: 4,
                        px: 3,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <IconButton onClick={() => navigate('/')} sx={{ color: 'white', mr: 1 }}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
                            Friend Invitation
                        </Typography>
                    </Box>
                </Box>

                {/* Login Prompt Card */}
                <Box sx={{ px: 3, mt: 4 }}>
                    <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                        <CardContent sx={{ textAlign: 'center', py: 6 }}>
                            <Avatar sx={{ bgcolor: '#E8F5F3', color: '#2D6E6A', width: 80, height: 80, mx: 'auto', mb: 3 }}>
                                <People sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                                You've Been Invited!
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 1 }}>
                                <strong>{pendingInviterEmail}</strong> wants to connect with you on
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2D6E6A', mb: 3 }}>
                                Percoro
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 4 }}>
                                Sign up or log in to accept the invitation and start sharing travel experiences!
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<PersonAdd />}
                                    onClick={handleSignupRedirect}
                                    sx={{
                                        bgcolor: '#2D6E6A',
                                        borderRadius: '25px',
                                        textTransform: 'none',
                                        px: 4,
                                        py: 1.5,
                                        fontWeight: 600,
                                        '&:hover': { bgcolor: '#009688' },
                                    }}
                                >
                                    Sign Up Free
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    startIcon={<Login />}
                                    onClick={handleLoginRedirect}
                                    sx={{
                                        borderColor: '#2D6E6A',
                                        color: '#2D6E6A',
                                        borderRadius: '25px',
                                        textTransform: 'none',
                                        px: 4,
                                        py: 1.5,
                                        fontWeight: 600,
                                        '&:hover': { borderColor: '#009688', bgcolor: '#E8F5F3' },
                                    }}
                                >
                                    Log In
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Features Preview */}
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                            What you can do on Percoro
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                            {[
                                { icon: <PhotoLibrary />, text: 'Share travel photos' },
                                { icon: <Flight />, text: 'Plan trips together' },
                                { icon: <People />, text: 'Connect with friends' },
                                { icon: <EventNote />, text: 'Coordinate itineraries' },
                            ].map((item, index) => (
                                <Card key={index} sx={{ minWidth: 150, borderRadius: '12px' }}>
                                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                        <Avatar sx={{ bgcolor: '#E8F5F3', color: '#2D6E6A', mx: 'auto', mb: 1, width: 40, height: 40 }}>
                                            {item.icon}
                                        </Avatar>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {item.text}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* Login Prompt Dialog */}
                <Dialog
                    open={loginPromptOpen}
                    onClose={() => setLoginPromptOpen(false)}
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <DialogTitle sx={{ fontWeight: 600, textAlign: 'center' }}>
                        Welcome to Percoro!
                    </DialogTitle>
                    <DialogContent>
                        <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
                            Log in or sign up to accept the friend invitation and start your travel journey.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                        <Button
                            variant="contained"
                            onClick={handleSignupRedirect}
                            sx={{ bgcolor: '#2D6E6A', borderRadius: '20px', textTransform: 'none', px: 3 }}
                        >
                            Sign Up
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleLoginRedirect}
                            sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', borderRadius: '20px', textTransform: 'none', px: 3 }}
                        >
                            Log In
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    // ============ AUTHENTICATED VIEW ============
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFB', pb: 4 }}>
            {/* Pending Invite Banner — only renders when pendingInviterEmail is set
                AND the inviter is not the current user (#217 Part 1 — guarded in useEffect) */}
{pendingInviterEmail && (
    <Alert
        severity="warning"
        variant="filled"
        sx={{
            borderRadius: 0,
            py: 2,
            bgcolor: '#FF9800',
            color: 'white',
            '& .MuiAlert-icon': { color: 'white' },
        }}
        icon={<PersonAdd />}
        action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                    variant="contained"
                    size="medium"
                    startIcon={<Check />}
                    onClick={processPendingInvite}
                    sx={{
                        bgcolor: 'white',
                        color: '#FF9800',
                        textTransform: 'none',
                        borderRadius: '20px',
                        fontWeight: 700,
                        px: 3,
                        '&:hover': { bgcolor: '#FFF3E0' },
                    }}
                >
                    Accept
                </Button>
                <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<Close />}
                    onClick={() => {
                        localStorage.removeItem('pendingFriendInvite');
                        setPendingInviterEmail(null);
                        setSnackbarMessage('Invitation declined');
                        setSnackbarOpen(true);
                    }}
                    sx={{
                        borderColor: 'white',
                        color: 'white',
                        textTransform: 'none',
                        borderRadius: '20px',
                        '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                >
                    Decline
                </Button>
            </Box>
        }
    >
        <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
            <strong>{pendingInviterEmail}</strong> wants to connect with you on Percoro!
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', opacity: 0.95 }}>
            Click <strong>Accept</strong> to add them to your circle.
        </Typography>
    </Alert>
)}

            {/* Header */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #2D6E6A 0%, #008B7A 100%)',
                    color: 'white',
                    pt: 3,
                    pb: 4,
                    px: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton onClick={() => {
                        const token = localStorage.getItem('idToken');
                        const fromSettings = searchParams.get('from') === 'settings';
        navigate(fromSettings ? '/settings' : (token ? '/explore' : '/'));
                    }} sx={{ color: 'white', mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
                        {searchParams.get('from') === 'settings' ? 'Friends' : 'Friends'}
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9, ml: 6 }}>
                    Connect with friends and family to share your travel experiences
                </Typography>
            </Box>

            <Box sx={{ px: 3, mt: 1, display: 'flex', gap: 2 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    <strong>{activeFriends.length}</strong> friends
                </Typography>
                {pendingTotal > 0 && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        <strong>{pendingTotal}</strong> pending
                    </Typography>
                )}
            </Box>

{/* Main Content */}
            <Box sx={{ px: 3, mt: 3 }}>
                {/* Search & Actions */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <TextField
                        placeholder="Search friends..."
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            flex: 1,
                            minWidth: 200,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                bgcolor: 'white',
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<PersonAdd />}
                        onClick={() => setInviteDialogOpen(true)}
                        sx={{
                            bgcolor: '#2D6E6A',
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            '&:hover': { bgcolor: '#009688' },
                        }}
                    >
                        Invite Friend
                    </Button>
                    {groupTripsEnabled && (
                    <Button
                        variant="outlined"
                        startIcon={<GroupAdd />}
                        onClick={() => setGroupDialogOpen(true)}
                        sx={{
                            borderColor: '#2D6E6A',
                            color: '#2D6E6A',
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { borderColor: '#009688', bgcolor: '#E8F5F3' },
                        }}
                    >
                        New Group
                    </Button>
                    )}
                </Box>

                {/* Tabs */}
                <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    sx={{
                        mb: 3,
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: 'auto',
                            px: 3,
                        },
                        '& .Mui-selected': { color: '#2D6E6A' },
                        '& .MuiTabs-indicator': { bgcolor: '#2D6E6A' },
                    }}
                >
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Person fontSize="small" />
                                Friends
                                <Chip label={activeFriends.length} size="small" sx={{ bgcolor: '#E8F5F3', color: '#2D6E6A', height: 20 }} />
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Send fontSize="small" />
                                Invites
                                {/* (#217 Part 1) Badge shows total pending (sent + received), not just received */}
                                {pendingTotal > 0 && (
                                    <Chip
                                        label={pendingTotal}
                                        size="small"
                                        sx={{
                                            bgcolor: pendingReceived.length > 0 ? '#F44336' : '#FF9800',
                                            color: 'white',
                                            height: 20,
                                        }}
                                    />
                                )}
                            </Box>
                        }
                    />
                    <Tab sx={{ display: groupTripsEnabled ? undefined : 'none' }}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Group fontSize="small" />
                                Groups
                                <Chip label={groups.length} size="small" sx={{ bgcolor: '#F3E5F5', color: '#4A6FA5', height: 20 }} />
                            </Box>
                        }
                    />
                </Tabs>

                {/* Tab Content */}
                {activeTab === 0 && (
                    <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                        {friendsLoading ? (
                            <CardContent sx={{ textAlign: 'center', py: 6 }}>
                                <CircularProgress sx={{ color: '#2D6E6A' }} />
                                <Typography color="text.secondary" sx={{ mt: 2 }}>
                                    Loading friends...
                                </Typography>
                            </CardContent>
                        ) : filteredFriends.filter(f => f.status === 'active').length === 0 ? (
                            <CardContent sx={{ textAlign: 'center', py: 6 }}>
                                <Avatar sx={{ bgcolor: '#E8F5F3', color: '#2D6E6A', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                                    <People sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    No friends yet
                                </Typography>
                                <Typography color="text.secondary" sx={{ mb: 3 }}>
                                    Invite someone to join Percoro and share your travels together
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<PersonAdd />}
                                    onClick={() => setInviteDialogOpen(true)}
                                    sx={{
                                        bgcolor: '#2D6E6A',
                                        borderRadius: '20px',
                                        textTransform: 'none',
                                        '&:hover': { bgcolor: '#009688' },
                                    }}
                                >
                                    Invite a Friend
                                </Button>
                            </CardContent>
                        ) : (
                            <List sx={{ p: 0 }}>
                                {filteredFriends.filter(f => f.status === 'active').map((friend, index, arr) => (
                                    <React.Fragment key={friend.id}>
                                        <ListItem sx={{ py: 2, px: 3 }}>
                                            <ListItemAvatar>
                                                <Avatar src={friend.avatar} sx={{ bgcolor: '#2D6E6A' }}>
                                                    {friend.name[0]}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography sx={{ fontWeight: 600 }}>
                                                        {friend.name}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {friend.email}
                                                        </Typography>
                                                        
                                                    </Box>
                                                }
                                            />
                                            <ListItemSecondaryAction>

                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        setAnchorEl(e.currentTarget);
                                                        setMenuFriendId(friend.id);
                                                    }}
                                                >
                                                    <MoreVert />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                        {index < arr.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Card>
                )}

                {activeTab === 1 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Received Invites */}
                        {pendingReceived.length > 0 && (
                            <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#2196F3' }}>
                                        Friend Requests ({pendingReceived.length})
                                    </Typography>
                                    <List sx={{ p: 0 }}>
                                        {pendingReceived.map((friend) => (
                                            <ListItem key={friend.id} sx={{ px: 0 }}>
                                                <ListItemAvatar>
                                                    <Avatar src={friend.avatar} sx={{ bgcolor: '#2196F3' }}>
                                                        {friend.name[0]}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={friend.name}
                                                    secondary={friend.email}
                                                />
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<Check />}
                                                        onClick={() => handleAcceptInvite(friend.id)}
                                                        sx={{
                                                            bgcolor: '#2D6E6A',
                                                            borderRadius: '20px',
                                                            textTransform: 'none',
                                                            '&:hover': { bgcolor: '#009688' },
                                                        }}
                                                    >
                                                        Accept
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<Close />}
                                                        onClick={() => handleDeclineInvite(friend.id)}
                                                        sx={{
                                                            borderColor: '#ccc',
                                                            color: 'text.secondary',
                                                            borderRadius: '20px',
                                                            textTransform: 'none',
                                                        }}
                                                    >
                                                        Decline
                                                    </Button>
                                                </Box>
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        )}

                        {/* Sent Invites */}
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                            <CardContent>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#FF9800' }}>
                                    Sent Invites ({friends.filter(f => f.status === 'pending_sent').length})
                                </Typography>
                                {friends.filter(f => f.status === 'pending_sent').length === 0 ? (
                                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                        No pending invites
                                    </Typography>
                                ) : (
                                    <List sx={{ p: 0 }}>
                                        {friends.filter(f => f.status === 'pending_sent').map((friend) => (
                                            <ListItem key={friend.id} sx={{ px: 0 }}>
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: '#FF9800' }}>
                                                        {friend.email[0].toUpperCase()}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={friend.email}
                                                    secondary={`Sent ${friend.addedDate}`}
                                                />
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Tooltip title="Resend invite">
                                                        <IconButton size="small" sx={{ color: '#FF9800' }}>
                                                            <Send fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel invite">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveFriend(friend.id)}
                                                            sx={{ color: 'text.secondary' }}
                                                        >
                                                            <Close fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {activeTab === 2 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {groups.length === 0 ? (
                            <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                                    <Avatar sx={{ bgcolor: '#F3E5F5', color: '#4A6FA5', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                                        <Group sx={{ fontSize: 32 }} />
                                    </Avatar>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        No travel groups yet
                                    </Typography>
                                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                                        Create a group to plan trips with friends and family
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<GroupAdd />}
                                        onClick={() => setGroupDialogOpen(true)}
                                        sx={{
                                            bgcolor: '#4A6FA5',
                                            borderRadius: '20px',
                                            textTransform: 'none',
                                            '&:hover': { bgcolor: '#3D5A8A' },
                                        }}
                                    >
                                        Create Your First Group
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            groups.map((group) => (
                                <Card
                                    key={group.id}
                                    sx={{
                                        borderRadius: '16px',
                                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                                        borderLeft: `4px solid ${group.color}`,
                                    }}
                                >
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                        {group.name}
                                                    </Typography>
                                                    <Chip
                                                        label={`${group.tripCount} trips`}
                                                        size="small"
                                                        sx={{ bgcolor: `${group.color}20`, color: group.color }}
                                                    />
                                                </Box>
                                                {group.description && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        {group.description}
                                                    </Typography>
                                                )}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 12 } }}>
                                                        {group.members.map((memberId) => {
                                                            const member = getFriendById(memberId);
                                                            return member ? (
                                                                <Avatar key={memberId} src={member.avatar} sx={{ bgcolor: group.color }}>
                                                                    {member.name[0]}
                                                                </Avatar>
                                                            ) : null;
                                                        })}
                                                    </AvatarGroup>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {group.members.length} member{group.members.length !== 1 ? 's' : ''}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
                                                >
                                                    {expandedGroup === group.id ? <ExpandLess /> : <ExpandMore />}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedGroup(group);
                                                        setNewGroupName(group.name);
                                                        setNewGroupDescription(group.description || '');
                                                        setSelectedMembers(group.members);
                                                        setGroupDialogOpen(true);
                                                    }}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteGroup(group.id)}
                                                    sx={{ color: 'error.main' }}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                        <Collapse in={expandedGroup === group.id}>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                                Members
                                            </Typography>
                                            <List dense sx={{ p: 0 }}>
                                                {group.members.map((memberId) => {
                                                    const member = getFriendById(memberId);
                                                    return member ? (
                                                        <ListItem key={memberId} sx={{ px: 0 }}>
                                                            <ListItemAvatar>
                                                                <Avatar src={member.avatar} sx={{ width: 32, height: 32, bgcolor: group.color }}>
                                                                    {member.name[0]}
                                                                </Avatar>
                                                            </ListItemAvatar>
                                                            <ListItemText
                                                                primary={member.name}
                                                                secondary={member.email}
                                                                primaryTypographyProps={{ variant: 'body2' }}
                                                                secondaryTypographyProps={{ variant: 'caption' }}
                                                            />
                                                        </ListItem>
                                                    ) : null;
                                                })}
                                            </List>
                                        </Collapse>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </Box>
                )}
            </Box>

            {/* Invite Friend Dialog */}
            <Dialog
                open={inviteDialogOpen}
                onClose={() => {
                    setInviteDialogOpen(false);
                    setInviteError(null);
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>
                    Invite a Friend
                </DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Send an invitation to connect on Percoro
                    </Typography>
                    {inviteError && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                            {inviteError}
                        </Alert>
                    )}
                    <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Email sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Personal Message (optional)"
                        multiline
                        rows={3}
                        value={inviteMessage}
                        onChange={(e) => setInviteMessage(e.target.value)}
                        placeholder="Add a personal note to your invitation..."
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button
                        onClick={() => {
                            setInviteDialogOpen(false);
                            setInviteError(null);
                        }}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSendInvite}
                        disabled={!inviteEmail || inviteLoading}
                        startIcon={inviteLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                        sx={{
                            bgcolor: '#2D6E6A',
                            borderRadius: '20px',
                            textTransform: 'none',
                            px: 3,
                            '&:hover': { bgcolor: '#009688' },
                        }}
                    >
                        {inviteLoading ? 'Sending...' : 'Send Invite'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create/Edit Group Dialog */}
            <Dialog
                open={groupDialogOpen}
                onClose={() => {
                    setGroupDialogOpen(false);
                    setSelectedGroup(null);
                    setNewGroupName('');
                    setNewGroupDescription('');
                    setSelectedMembers([]);
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>
                    {selectedGroup ? 'Edit Group' : 'Create Travel Group'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Group Name"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        sx={{ mb: 2, mt: 1 }}
                        placeholder="e.g., Family Vacation 2026"
                    />
                    <TextField
                        fullWidth
                        label="Description (optional)"
                        value={newGroupDescription}
                        onChange={(e) => setNewGroupDescription(e.target.value)}
                        sx={{ mb: 3 }}
                        placeholder="What's this group for?"
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Add Members
                    </Typography>
                    {activeFriends.length === 0 ? (
                        <Alert severity="info" sx={{ borderRadius: '8px' }}>
                            You need to add friends first before creating a group
                        </Alert>
                    ) : (
                        <FormGroup>
                            {activeFriends.map((friend) => (
                                <FormControlLabel
                                    key={friend.id}
                                    control={
                                        <Checkbox
                                            checked={selectedMembers.includes(friend.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedMembers([...selectedMembers, friend.id]);
                                                } else {
                                                    setSelectedMembers(selectedMembers.filter(id => id !== friend.id));
                                                }
                                            }}
                                            sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar src={friend.avatar} sx={{ width: 24, height: 24 }}>
                                                {friend.name[0]}
                                            </Avatar>
                                            {friend.name}
                                        </Box>
                                    }
                                />
                            ))}
                        </FormGroup>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button
                        onClick={() => {
                            setGroupDialogOpen(false);
                            setSelectedGroup(null);
                            setNewGroupName('');
                            setNewGroupDescription('');
                            setSelectedMembers([]);
                        }}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateGroup}
                        disabled={!newGroupName}
                        startIcon={<GroupAdd />}
                        sx={{
                            bgcolor: '#4A6FA5',
                            borderRadius: '20px',
                            textTransform: 'none',
                            px: 3,
                            '&:hover': { bgcolor: '#3D5A8A' },
                        }}
                    >
                        {selectedGroup ? 'Save Changes' : 'Create Group'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Permissions Dialog */}
            <Dialog
                open={permissionsDialogOpen}
                onClose={() => {
                    setPermissionsDialogOpen(false);
                    setSelectedFriend(null);
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>
                    Manage Access for {selectedFriend?.name}
                </DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Control what {selectedFriend?.name?.split(' ')[0]} can see from your profile
                    </Typography>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectedFriend?.canSeeTrips || false}
                                    onChange={(e) => setSelectedFriend(prev =>
                                        prev ? { ...prev, canSeeTrips: e.target.checked } : null
                                    )}
                                    sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Flight sx={{ color: '#2D6E6A' }} />
                                    <Box>
                                        <Typography sx={{ fontWeight: 500 }}>View My Trips</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Can see your trip destinations and dates
                                        </Typography>
                                    </Box>
                                </Box>
                            }
                            sx={{ mb: 2, alignItems: 'flex-start' }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectedFriend?.canSeePhotos || false}
                                    onChange={(e) => setSelectedFriend(prev =>
                                        prev ? { ...prev, canSeePhotos: e.target.checked } : null
                                    )}
                                    sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PhotoLibrary sx={{ color: '#2D6E6A' }} />
                                    <Box>
                                        <Typography sx={{ fontWeight: 500 }}>View My Photos</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Can see photos from your trips
                                        </Typography>
                                    </Box>
                                </Box>
                            }
                            sx={{ mb: 2, alignItems: 'flex-start' }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectedFriend?.canSeeItinerary || false}
                                    onChange={(e) => setSelectedFriend(prev =>
                                        prev ? { ...prev, canSeeItinerary: e.target.checked } : null
                                    )}
                                    sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EventNote sx={{ color: '#2D6E6A' }} />
                                    <Box>
                                        <Typography sx={{ fontWeight: 500 }}>View My Itinerary</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Can see detailed day-by-day plans
                                        </Typography>
                                    </Box>
                                </Box>
                            }
                            sx={{ alignItems: 'flex-start' }}
                        />
                    </FormGroup>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button
                        onClick={() => {
                            setPermissionsDialogOpen(false);
                            setSelectedFriend(null);
                        }}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUpdatePermissions}
                        sx={{
                            bgcolor: '#2D6E6A',
                            borderRadius: '20px',
                            textTransform: 'none',
                            px: 3,
                            '&:hover': { bgcolor: '#009688' },
                        }}
                    >
                        Save Permissions
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Friend Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem onClick={() => { setPokeModalOpen(true); setAnchorEl(null); }} sx={{ fontSize: '0.85rem', gap: 1.5 }}>
                    <CampaignIcon fontSize="small" sx={{ mr: 1, color: '#2D6E6A' }} />
                    Poke
                </MenuItem>
                <MenuItem onClick={() => { setAnchorEl(null); navigate(`/trips?shareWith=${menuFriendId}`); }} sx={{ fontSize: '0.85rem', gap: 1.5 }}>
                    <Send fontSize="small" sx={{ mr: 1 }} />
                    Share a Trip
                </MenuItem>
                <MenuItem onClick={() => handleRemoveFriend(menuFriendId!)}>
                    <Delete fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
                    <Typography color="error">Remove Friend</Typography>
                </MenuItem>
            </Menu>

            <PokeFriendsModal
                open={pokeModalOpen}
                onClose={() => setPokeModalOpen(false)}
                preSelectedFriendId={menuFriendId || undefined}
                onSuccess={(count) => { setSnackbarMessage(`Poked ${count} friend${count !== 1 ? 's' : ''}!`); setSnackbarOpen(true); }}
            />

            {/* Email Mismatch Dialog */}
            <Dialog open={Boolean(emailMismatch)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonAdd sx={{ color: '#2D6E6A' }} /> Wrong Account
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                        This invitation was sent to a different email address.
                    </Alert>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>{emailMismatch?.inviterEmail}</strong> invited <strong>{emailMismatch?.expectedEmail}</strong> to connect on Percoro.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        You're currently logged in as a different account. Please log out and sign in with <strong>{emailMismatch?.expectedEmail}</strong> to accept this invitation.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button fullWidth variant="contained"
                        onClick={() => {
                            localStorage.removeItem('idToken');
                            localStorage.removeItem('user');
                            navigate(`/login?redirect=/friends&invite=${emailMismatch?.inviterEmail}&to=${emailMismatch?.expectedEmail}`);
                        }}
                        sx={{ bgcolor: '#2D6E6A', borderRadius: '12px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#009688' } }}>
                        Log in as {emailMismatch?.expectedEmail}
                    </Button>
                    <Button fullWidth variant="outlined"
                        onClick={() => setEmailMismatch(null)}
                        sx={{ borderRadius: '12px', textTransform: 'none' }}>
                        Stay logged in as current account
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
};

export default FriendsPage;
